import { FC, useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import type { Role } from "@services/permissions/commands/cmdListRoles";

interface Props {
  show: boolean;
  onClose: () => void;
  onSave: (roleIds: number[]) => void;
  userName: string;
  currentRoleIds: number[];
  allRoles: Role[];
  isSaving: boolean;
}

const ManageRolesModal: FC<Props> = ({ show, onClose, onSave, userName, currentRoleIds, allRoles, isSaving }) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (show) setSelectedIds(new Set(currentRoleIds));
  }, [show, currentRoleIds]);

  const toggle = (roleId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  };

  const handleSave = () => onSave([...selectedIds]);

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Manage Roles</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Select roles for <strong>{userName}</strong>
        </p>
        {allRoles.length === 0 ? (
          <p className="text-muted text-center py-3 mb-0">No roles available.</p>
        ) : (
          <div className="d-flex flex-column gap-2">
            {allRoles.map((role) => (
              <div
                key={role.id}
                className={`border rounded p-2 px-3 d-flex align-items-center gap-2 ${
                  selectedIds.has(role.id) ? "border-primary bg-primary bg-opacity-10" : ""
                }`}
                style={{ cursor: "pointer" }}
                onClick={() => toggle(role.id)}
              >
                <Form.Check
                  type="checkbox"
                  checked={selectedIds.has(role.id)}
                  onChange={() => toggle(role.id)}
                  id={`manage-role-${role.id}`}
                  className="pe-none"
                />
                <span className="small fw-medium">{role.name}</span>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <><span className="spinner-border spinner-border-sm me-2" /> Saving...</>
          ) : (
            <><i className="bi bi-check-circle me-2" />Save Roles</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManageRolesModal;
